import { useCallback, useMemo } from 'react';
import { Platform, StyleSheet } from 'react-native';
import { IconLink, theme, useResponsive, useSafeRouter } from '@sg/garnish';

import {
  DEFAULT_SCREEN_OPTIONS,
  SCREEN_HEADER_OPTIONS,
  type ScreenOptionsMap,
} from '../../../../constants';
import { useRouteTitles } from '../../../messages';

// ─────────────────────────────────────────────────────────────────────────────

export const useAccountTabStackOptions = () => {
  const { currentBreakpoint } = useResponsive();
  const titles = useRouteTitles();

  // ─────────────────────────────────────────────────────────────────────

  const stackOptions = useMemo(
    () => ({
      ...DEFAULT_SCREEN_OPTIONS,
      ...SCREEN_HEADER_OPTIONS,
      headerShown: currentBreakpoint.isXS,
      headerTitle: undefined,
      headerLeft: () => <AccountHeaderBack />,
    }),
    [currentBreakpoint.isXS],
  );

  // ─────────────────────────────────────────────────────────────────────

  return useMemo(() => {
    return {
      //
      // ─── Main Stack Options (applied to all screens) ─────────────────────

      Stack: stackOptions,

      // ─── Individual Tabs ─────────────────────────────────────────────────

      Profile: { title: titles.profileTitle },
      PaymentAndGiftCards: { title: titles.paymentsTitle },
      CreditAndPromoCodes: { title: titles.creditAndPromoCodesTitle },
      GiftCards: { title: titles.giftCardsTitle },
      Addresses: { title: titles.addressesTitle },
      Favorites: { title: titles.favoritesTitle },
      DietaryRestrictions: { title: titles.dietaryRestrictionsTitle },
      Notifications: { title: titles.notificationsTitle },
    } satisfies ScreenOptionsMap;
  }, [stackOptions, titles]);
};

// ─── Components ─────────────────────────────────────────────────────────────

const AccountHeaderBack = () => {
  const router = useSafeRouter();

  const handlePress = useCallback(() => {
    if (router?.canGoBack()) {
      router.back();
      return;
    }

    router?.navigate('/account/menu');
  }, [router]);

  return (
    <IconLink
      name="IconArrowLeft"
      style={styles.backButton}
      color={theme.colors.GREEN_1}
      accessibilityLabel="Back"
      onPress={handlePress}
    />
  );
};

// ─── Constants ──────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  backButton: {
    paddingLeft: Platform.OS === 'web' ? theme.spacing['4'] : 0,
  },
});
